import { state, setMap, els } from "./state.js";
import { showMapLoading, hideMapLoading } from "../../shared/map/loading-utils.js";

/**
 * Calculate center coordinates from boundary data
 * @param {Array} boundaryData - Array of boundary coordinates
 * @returns {Array} [lat, lng] center coordinates
 */
function calculateCenterFromBoundary(boundaryData) {
  if (!boundaryData || boundaryData.length === 0) {
    return [10.8231, 106.6297]; // Fallback to Ho Chi Minh City
  }

  let minLat = Infinity,
    maxLat = -Infinity;
  let minLng = Infinity,
    maxLng = -Infinity;

  boundaryData.forEach((coord) => {
    if (coord && coord.length >= 2) {
      const [lng, lat] = coord;
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
      minLng = Math.min(minLng, lng);
      maxLng = Math.max(maxLng, lng);
    }
  });

  if (minLat === Infinity || maxLat === -Infinity) {
    return [10.8231, 106.6297]; // Fallback
  }

  const centerLat = (minLat + maxLat) / 2;
  const centerLng = (minLng + maxLng) / 2;

  console.log("Calculated center from boundary:", [centerLat, centerLng]);
  return [centerLat, centerLng];
}

/**
 * Initialize map with calculated center from boundary
 * @param {Array} center - [lat, lng] coordinates
 * @param {number} zoom - Zoom level
 */
function initializeMapWithCenter(center, zoom = 10) {
  // Prevent multiple initialization
  if (state.map) {
    state.map.remove();
  }

  console.log("Initializing map with calculated center:", center, "zoom:", zoom);

  const mapInstance = L.map("osm-wrapper", {
    center: center,
    zoom: zoom,
    zoomControl: true,
  });

  setMap(mapInstance);

  L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    attribution:
      "&copy; <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors",
  }).addTo(state.map);

  // Add locate control with custom options
  try {
    if (typeof L.control.locate === "function") {
      const locateControl = L.control
        .locate({
          position: "topleft",
          strings: {
            title: "Show my location",
          },
          locateOptions: {
            enableHighAccuracy: true,
            maxZoom: 16,
            watch: true,
            setView: "untilPan",
          },
        })
        .addTo(state.map);
      console.log("Locate control added successfully");
    } else {
      console.warn("Locate control plugin not available");
    }
  } catch (error) {
    console.warn("Error adding locate control:", error);
  }

  // Add reset map control
  var resetMapControl = L.control({ position: "topright" });
  resetMapControl.onAdd = function (map) {
    var div = L.DomUtil.create("div", "reset-map-control");
    div.innerHTML = `<button id="reset-map">${mpsString.view_all_ip_btn_text}</button>`;
    L.DomEvent.on(div, "click", function (e) {
      L.DomEvent.stopPropagation(e);
      resetMap();
    });
    return div;
  };
  resetMapControl.addTo(state.map);

  // Dispatch map ready event
  setTimeout(() => {
    const event = new CustomEvent("mapReady", { detail: { map: state.map } });
    document.dispatchEvent(event);
    console.log("Map ready event dispatched");
  }, 100);

  return mapInstance;
}

/**
 * Create map container without initializing map
 * This prevents showing wrong default location
 */
export function createMapContainer() {
  console.log("Creating map container without initialization");

  // Ensure container exists
  const container = document.getElementById("osm-wrapper");
  if (!container) {
    console.error("Map container #osm-wrapper not found");
    return false;
  }

  // Show the existing loading indicator using shared utility
  showMapLoading();
  console.log("Showing existing loading indicator");

  // Clear container content but keep the structure
  container.innerHTML = "";

  return true;
}

/**
 * Initialize map with boundary-based center calculation
 * @param {Array} boundaryData - Boundary coordinates array
 * @param {number} zoom - Zoom level
 */
export function initMapWithBoundary(boundaryData, zoom = 10) {
  console.log("Initializing map with boundary data");

  // Calculate center from boundary
  const center = calculateCenterFromBoundary(boundaryData);

  // Initialize map with calculated center
  const mapInstance = initializeMapWithCenter(center, zoom);

  // Hide the existing loading indicator using shared utility
  hideMapLoading();
  console.log("Hiding loading indicator");

  return mapInstance;
}

/**
 * Legacy function for backward compatibility
 * @deprecated Use initMapWithBoundary instead
 */
export function initMap() {
  console.warn("initMap() is deprecated. Use initMapWithBoundary() instead.");
  return initializeMapWithCenter([10.8231, 106.6297], 10);
}

export function resetMap() {
  try {
    if (state.boundaryLayer) {
      state.boundaryLayer.setStyle({ fillOpacity: 0.1 });
      state.map.flyToBounds(state.boundaryLayer.getBounds(), {
        animate: true,
        duration: 1.5,
      });
    } else if (
      state.bounds &&
      typeof state.bounds.isEmpty === "function" &&
      !state.bounds.isEmpty()
    ) {
      state.map.flyToBounds(state.bounds, {
        animate: true,
        duration: 1.5,
        maxZoom: 14,
      });
    } else if (state.bounds && state.bounds.getNorthEast && state.bounds.getSouthWest) {
      // Alternative bounds checking
      const ne = state.bounds.getNorthEast();
      const sw = state.bounds.getSouthWest();
      if (ne && sw && ne.lat !== sw.lat && ne.lng !== sw.lng) {
        state.map.flyToBounds(state.bounds, {
          animate: true,
          duration: 1.5,
          maxZoom: 14,
        });
      } else {
        console.log("No valid bounds available for reset");
      }
    } else {
      console.log("No bounds available for reset");
    }
  } catch (error) {
    console.error("Error in resetMap:", error);
  }
}
